/* ===========================================================================
   ZYREX — assets/facets.js
   <facet-form> and <price-range> — filtering and sorting on collection and
   search pages.

   Loaded after assets/theme.js, so window.ZUtils and window.Zyrex are already
   there. Every use of them is still guarded: one missing global must never
   take the filter form down, because the form is the fallback.

   WITHOUT THIS FILE
     snippets/facets.liquid renders one GET form holding the sort select,
     every checkbox and both price fields. Submitting it applies everything at
     once and the page reloads. That is the whole feature, working, with no
     script at all. Everything below is an upgrade on top of it.

   WHAT THE UPGRADE DOES
     · a change to any control queues an apply, debounced so a burst of
       clicks or a drag becomes one request
     · the apply fetches the page through the Section Rendering API and swaps
       three regions in place — the filter form, the results and any element
       showing the count — so the scroll position and the page around it stay
       exactly where they were
     · history is pushed, and Back re-renders from the URL it lands on
     · focus is put back on the control that was being used, so keyboard and
       screen reader users are not dropped at the top of the document
     · the results dim and are marked aria-busy while a request is out, and
       the new count is announced when it lands
     · below 1024px the panel is a sheet: the Filters button opens it, focus
       is trapped inside it, Escape and the backdrop close it

   MARKUP CONTRACT (all of it produced by snippets/facets.liquid)
     <facet-form data-facets>
       <form data-facets-form action=…>
         [data-facets-count]     the live figure
         [data-facets-open]      opens the sheet (aria-expanded, aria-controls)
         [data-facets-sort]      the sort select
         [data-facets-panel]     the panel, `.is-open` while the sheet is up
         [data-facets-close]     closes the sheet
         [data-facets-input]     every checkbox and price field
         [data-facets-remove]    a link that removes one value
         [data-facets-clear]     a link that removes them all
       </form>
       [data-facets-backdrop] / .facets__backdrop
     </facet-form>

   THE SECTION SIDE
     [data-facets-results]   the grid, its pagination and its empty state
     [data-facets-toolbar]   optional extra chrome outside the form
   Both are swapped by index against the same selector in the response. If a
   section exposes neither, the whole section body is replaced instead, so a
   template that forgets the attributes still filters correctly.

   PRICE RANGE
     Two number fields are the source of truth — they carry the names, they
     are what submits, and they are what a person without script uses. The
     dual-handle track is a convenience layered over them: dragging a handle
     writes the field and fires a change, and the two handles clamp against
     each other so the range can never invert.
   =========================================================================== */

(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var win = window;
  var doc = document;

  if (!('customElements' in win) || typeof win.HTMLElement !== 'function') return;

  /* =======================================================================
     0 — Constants
     ======================================================================= */

  /* Long enough that ticking three boxes in a row is one request, short
     enough that a single click still feels immediate. */
  var APPLY_DEBOUNCE = 350;

  /* Matches the breakpoint component-facets.css switches the panel at. */
  var SHEET_QUERY = '(max-width: 1023.98px)';

  /* Swapped in order, paired by index against the response. */
  var REGIONS = ['[data-facets]', '[data-facets-results]', '[data-facets-toolbar]'];

  /* =======================================================================
     1 — Small helpers
     ======================================================================= */

  function isFn(value) {
    return typeof value === 'function';
  }

  function qs(selector, scope) {
    try {
      return (scope || doc).querySelector(selector);
    } catch (error) {
      return null;
    }
  }

  function qsa(selector, scope) {
    try {
      return Array.prototype.slice.call((scope || doc).querySelectorAll(selector));
    } catch (error) {
      return [];
    }
  }

  function utils() {
    return win.ZUtils || null;
  }

  function config() {
    return win.Zyrex || {};
  }

  /** A translated UI string with a plain English fallback. */
  function string(group, key, fallback) {
    var all = config().strings;
    if (all && all[group] && all[group][key]) return all[group][key];
    return fallback;
  }

  function announce(message) {
    var helpers = utils();
    if (!message) return;
    if (helpers && isFn(helpers.announce)) helpers.announce(message);
  }

  function debounce(fn, wait) {
    var helpers = utils();
    if (helpers && isFn(helpers.debounce)) return helpers.debounce(fn, wait);

    var timer = null;
    function debounced() {
      var context = this;
      var args = arguments;
      if (timer) win.clearTimeout(timer);
      timer = win.setTimeout(function () {
        timer = null;
        fn.apply(context, args);
      }, wait);
    }
    debounced.cancel = function () {
      if (timer) win.clearTimeout(timer);
      timer = null;
    };
    return debounced;
  }

  function toNumber(value, fallback) {
    var parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : fallback;
  }

  function clamp(value, min, max) {
    if (value < min) return min;
    if (value > max) return max;
    return value;
  }

  function isModifiedClick(event) {
    return (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    );
  }

  function canFetch() {
    return isFn(win.fetch) && isFn(win.DOMParser) && isFn(win.URLSearchParams);
  }

  /** Fire an event that both the facet form and any other listener can hear. */
  function emit(target, type) {
    if (!target || !isFn(target.dispatchEvent)) return;
    try {
      target.dispatchEvent(new win.Event(type, { bubbles: true }));
    } catch (error) {
      /* Event is unconstructable in this host — nothing depends on it */
    }
  }

  /**
   * Define an element once. The Theme Editor can evaluate this file again
   * after a section reload, and a second define() with the same name throws.
   */
  function define(name, ctor) {
    if (!name || !ctor) return;
    try {
      if (win.customElements.get(name)) return;
      win.customElements.define(name, ctor);
    } catch (error) {
      /* an already-defined name in a host we do not control is not fatal */
    }
  }

  /* A minimal base class: it collects listeners and timers so a disconnect is
     one call and nothing is left running. */
  var FacetElement = (function () {
    if (typeof win.HTMLElement !== 'function') return null;
    return class ZyrexFacetElement extends win.HTMLElement {};
  })();

  if (!FacetElement) return;

  FacetElement.prototype.listen = function (target, type, handler, options) {
    if (!target || !isFn(target.addEventListener)) return handler;
    if (!this._listeners) this._listeners = [];
    target.addEventListener(type, handler, options);
    this._listeners.push([target, type, handler, options]);
    return handler;
  };

  FacetElement.prototype.teardown = function () {
    (this._listeners || []).forEach(function (item) {
      try {
        item[0].removeEventListener(item[1], item[2], item[3]);
      } catch (error) {
        /* a node that has already gone needs nothing */
      }
    });
    this._listeners = [];
  };

  /* =======================================================================
     2 — <facet-form>
     ======================================================================= */

  class FacetForm extends FacetElement {
    connectedCallback() {
      this.collectRefs();
      if (!this.form) return;

      this.section = this.closest ? this.closest('.shopify-section') : null;
      this.sectionId = this.readSectionId();
      this.request = null;
      this.panelOpen = false;
      this.trapped = false;
      this.pending = '';
      this.pendingFallback = false;

      this.queueApply = debounce(this.applyFromForm.bind(this), APPLY_DEBOUNCE);

      /* Every listener is delegated from the host element, so swapping the
         inner markup never leaves a dead handler or loses a live one. */
      this.listen(this, 'change', this.onChange.bind(this));
      this.listen(this, 'input', this.onInput.bind(this));
      this.listen(this, 'click', this.onClick.bind(this));
      this.listen(this, 'keydown', this.onKeydown.bind(this));
      this.listen(this, 'submit', this.onSubmit.bind(this));
      this.listen(win, 'popstate', this.onPopState.bind(this));

      try {
        this.mq = win.matchMedia(SHEET_QUERY);
        var self = this;
        this.onMediaChange = function () {
          if (!self.mq.matches && self.panelOpen) self.closePanel(false);
        };
        if (isFn(this.mq.addEventListener)) {
          this.listen(this.mq, 'change', this.onMediaChange);
        } else if (isFn(this.mq.addListener)) {
          this.mq.addListener(this.onMediaChange);
          this._legacyMedia = true;
        }
      } catch (error) {
        this.mq = null;
      }

      this.setAttribute('data-facets-ready', 'true');
    }

    disconnectedCallback() {
      this.teardown();
      if (this.queueApply && isFn(this.queueApply.cancel)) this.queueApply.cancel();
      if (this._legacyMedia && this.mq && isFn(this.mq.removeListener)) {
        this.mq.removeListener(this.onMediaChange);
      }
      if (this.request && isFn(this.request.abort)) this.request.abort();
      if (this.trapped) this.releaseSheet();
    }

    /* ---- References ----------------------------------------------------
       Re-read after every swap: the form, the panel, the backdrop and the
       opener are all replaced when this element's markup is exchanged, so a
       reference taken once would be pointing at a detached node. */

    collectRefs() {
      this.form = qs('[data-facets-form]', this) || qs('form', this);
      this.panel = qs('[data-facets-panel]', this);
      this.opener = qs('[data-facets-open]', this);
      this.backdrop = qs('[data-facets-backdrop]', this) || qs('.facets__backdrop', this);
    }

    readSectionId() {
      var explicit = this.getAttribute('data-section-id');
      if (explicit) return explicit;
      if (this.section && this.section.id) {
        return this.section.id.replace(/^shopify-section-/, '');
      }
      return '';
    }

    scope() {
      return this.section || doc;
    }

    /* ---- Events --------------------------------------------------------- */

    onChange(event) {
      var target = event.target;
      if (!target || !isFn(target.closest)) return;
      if (target.closest('[data-facets-sort]') || target.closest('[data-facets-input]')) {
        this.remember(target);
        this.queueApply();
      }
    }

    onInput(event) {
      var target = event.target;
      if (!target || !isFn(target.closest)) return;
      /* Number fields fire input on every keystroke; the debounce is what
         makes that one request rather than five. */
      if (target.closest('[data-facets-input]') && target.type === 'number') {
        this.remember(target);
        this.queueApply();
      }
    }

    onSubmit(event) {
      if (!canFetch() || !this.form) return;
      event.preventDefault();
      if (this.queueApply && isFn(this.queueApply.cancel)) this.queueApply.cancel();
      if (this.panelOpen) this.closePanel(true);
      this.applyFromForm();
    }

    onKeydown(event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (!this.panelOpen) return;
      event.preventDefault();
      this.closePanel(true);
    }

    onClick(event) {
      var target = event.target;
      if (!target || !isFn(target.closest)) return;

      if (target.closest('[data-facets-open]')) {
        event.preventDefault();
        this.openPanel();
        return;
      }

      if (target.closest('[data-facets-close]')) {
        event.preventDefault();
        this.closePanel(true);
        return;
      }

      /* A chip and the clear link are ordinary links to a URL that removes
         something. With script they become an in-place apply. */
      var link = target.closest('[data-facets-remove], [data-facets-clear]');
      if (link && link.tagName === 'A') {
        if (isModifiedClick(event) || !canFetch()) return;
        event.preventDefault();
        if (this.queueApply && isFn(this.queueApply.cancel)) this.queueApply.cancel();
        /* The chip that was clicked is about to stop existing, so focus has
           to be handed to whatever takes its place. */
        this.pending = '';
        this.pendingFallback = true;
        this.apply(link.getAttribute('href'), { history: 'push' });
      }
    }

    onPopState() {
      if (!this.isConnected || !canFetch()) return;
      this.pending = '';
      this.pendingFallback = false;
      this.apply(win.location.pathname + win.location.search, { history: 'none' });
    }

    /* ---- Focus ----------------------------------------------------------
       The control being used is described before the swap and found again
       after it, so a filter applied with the keyboard leaves the keyboard
       exactly where it was. */

    /** Describe a control well enough to find its replacement afterwards. */
    describe(node) {
      if (!node || !isFn(node.getAttribute)) return '';

      var quote = function (value) {
        return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      };

      if (node.id) return '[id="' + quote(node.id) + '"]';

      var name = node.getAttribute('name');
      if (name) {
        var selector = '[name="' + quote(name) + '"]';
        var value = node.getAttribute('value');
        if (value !== null && node.type === 'checkbox') {
          selector += '[value="' + quote(value) + '"]';
        }
        return selector;
      }

      /* A slider handle carries neither, so its own attribute is the key. */
      var handle = node.getAttribute('data-prange-handle');
      if (handle) return '[data-prange-handle="' + quote(handle) + '"]';

      return '';
    }

    /**
     * The control that should hold focus after the swap: whatever is focused
     * right now if it belongs to this element, otherwise the control that
     * fired the change.
     */
    remember(target) {
      var active = doc.activeElement;
      var node = active && active !== doc.body && this.contains(active) ? active : target;
      this.pending = this.describe(node);
      this.pendingFallback = false;
    }

    findRemembered() {
      var found = this.pending ? qs(this.pending, this) : null;
      if (found) return found;
      if (!this.pendingFallback) return null;
      /* Nothing to go back to: the nearest sensible landing place instead. */
      return qs('[data-facets-clear]', this) || qs('[data-facets-open]', this) || null;
    }

    /* ---- Applying -------------------------------------------------------- */

    /** Everything the form holds, minus the empty fields, as a URL. */
    buildUrl() {
      var form = this.form;
      var action = form.getAttribute('action') || win.location.pathname;

      var search;
      try {
        var data = new win.FormData(form);
        search = new win.URLSearchParams();
        data.forEach(function (value, key) {
          if (typeof value !== 'string') return;
          if (value.trim() === '') return;
          search.append(key, value);
        });
      } catch (error) {
        return null;
      }

      var query = search.toString();
      return query ? action + '?' + query : action;
    }

    applyFromForm() {
      if (!this.form) return;

      if (!canFetch()) {
        /* No fetch in this host: fall back to a real submit, which is the
           same result with a page load. */
        if (isFn(this.form.requestSubmit)) this.form.requestSubmit();
        else this.form.submit();
        return;
      }

      var url = this.buildUrl();
      if (!url) return;
      this.apply(url, { history: 'push' });
    }

    requestUrl(url) {
      var helpers = utils();
      if (this.sectionId && helpers && isFn(helpers.sectionUrl)) {
        return helpers.sectionUrl(this.sectionId, { url: url });
      }
      if (this.sectionId) {
        return url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(this.sectionId);
      }
      /* No section id: the whole page is fetched and the same regions are
         lifted out of it. Heavier, still correct. */
      return url;
    }

    apply(url, options) {
      if (!url || !canFetch()) return;

      var opts = options || {};
      var self = this;

      if (this.request && isFn(this.request.abort)) this.request.abort();
      if (isFn(win.AbortController)) this.request = new win.AbortController();

      this.setBusy(true);

      var init = { headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' } };
      if (this.request && this.request.signal) init.signal = this.request.signal;

      win
        .fetch(this.requestUrl(url), init)
        .then(function (response) {
          if (!response.ok) throw new Error('Request failed');
          return response.text();
        })
        .then(function (html) {
          self.request = null;
          var replaced = self.render(html);

          if (opts.history === 'push') self.pushUrl(url);
          else if (opts.history === 'replace') self.pushUrl(url, true);

          if (!replaced) return;

          self.collectRefs();
          self.restoreState();
          self.setBusy(false);
          self.refreshMotion();
          self.announceResults();
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          self.request = null;
          self.setBusy(false);
          /* The filter must still work. A failed swap becomes a navigation to
             the same URL, which is what would have happened without script. */
          win.location.href = url;
        });
    }

    /** Swap the regions. Returns false when the whole section was replaced. */
    render(html) {
      var parsed;
      try {
        parsed = new win.DOMParser().parseFromString(html, 'text/html');
      } catch (error) {
        return true;
      }

      var incomingSection = this.sectionId
        ? parsed.getElementById('shopify-section-' + this.sectionId)
        : null;
      var source = incomingSection || parsed.body;
      var target = this.scope();
      var swapped = false;

      REGIONS.forEach(function (selector) {
        var current = qsa(selector, target);
        var next = qsa(selector, source);
        var count = Math.min(current.length, next.length);
        for (var i = 0; i < count; i++) {
          current[i].innerHTML = next[i].innerHTML;
          swapped = true;
        }
      });

      if (!swapped) {
        /* Nothing was marked up for swapping. Replace the section body so the
           page is still right, and stop: this element is gone with it. */
        if (incomingSection && this.section) {
          this.section.innerHTML = incomingSection.innerHTML;
          this.refreshMotion(this.section);
          return false;
        }
        return true;
      }

      /* Counts that live outside the swapped regions. */
      var counts = qsa('[data-facets-count]', target);
      var nextCounts = qsa('[data-facets-count]', source);
      var total = Math.min(counts.length, nextCounts.length);
      for (var c = 0; c < total; c++) {
        counts[c].textContent = nextCounts[c].textContent;
      }

      return true;
    }

    pushUrl(url, replace) {
      if (!win.history) return;
      try {
        var state = { zyrexFacets: true };
        if (replace && isFn(win.history.replaceState)) win.history.replaceState(state, '', url);
        else if (isFn(win.history.pushState)) win.history.pushState(state, '', url);
      } catch (error) {
        /* history is unavailable — the page is still correct */
      }
    }

    /** Put the sheet back the way it was and hand focus back. */
    restoreState() {
      var restored = this.findRemembered();

      if (this.panelOpen && this.panel) {
        this.panel.classList.add('is-open');
        if (this.backdrop) this.backdrop.classList.add('is-open');
        if (this.opener) this.opener.setAttribute('aria-expanded', 'true');

        var helpers = utils();
        if (this.trapped && helpers && isFn(helpers.removeTrapFocus) && isFn(helpers.trapFocus)) {
          /* The panel that was trapped no longer exists, so the trap is
             popped and pushed again around its replacement. */
          helpers.removeTrapFocus(this.panel);
          helpers.trapFocus(this.panel, restored || null);
          this.pending = '';
          this.pendingFallback = false;
          return;
        }
      }

      if (restored && isFn(restored.focus)) {
        try {
          restored.focus({ preventScroll: true });
        } catch (error) {
          restored.focus();
        }
      }
      this.pending = '';
      this.pendingFallback = false;
    }

    setBusy(flag) {
      this.classList.toggle('is-loading', !!flag);
      this.setAttribute('aria-busy', flag ? 'true' : 'false');

      qsa('[data-facets-results]', this.scope()).forEach(function (region) {
        region.classList.toggle('is-loading', !!flag);
        region.setAttribute('aria-busy', flag ? 'true' : 'false');
      });
    }

    refreshMotion(node) {
      if (!win.ZAnim || !isFn(win.ZAnim.refresh)) return;
      try {
        win.ZAnim.refresh(node || this.scope());
      } catch (error) {
        /* motion is decoration; a failure here changes nothing else */
      }
    }

    announceResults() {
      var counter = qs('[data-facets-count]', this.scope());
      var figure = counter ? String(counter.textContent || '').trim() : '';
      if (!figure) return;

      if (figure === '0') {
        announce(string('filters', 'noResults', 'No results'));
        return;
      }

      announce(figure + ' ' + string('filters', 'results', 'results'));
    }

    /* ---- The sheet ------------------------------------------------------- */

    openPanel() {
      if (!this.panel || this.panelOpen) return;

      this.panelOpen = true;
      this.panel.classList.add('is-open');
      if (this.backdrop) this.backdrop.classList.add('is-open');
      if (this.opener) this.opener.setAttribute('aria-expanded', 'true');

      var helpers = utils();
      if (this.isSheet()) {
        if (helpers && isFn(helpers.lockScroll)) helpers.lockScroll();
        if (helpers && isFn(helpers.trapFocus)) helpers.trapFocus(this.panel);
        this.trapped = true;
      }
    }

    closePanel(returnFocus) {
      if (!this.panelOpen) return;

      this.panelOpen = false;
      if (this.panel) this.panel.classList.remove('is-open');
      if (this.backdrop) this.backdrop.classList.remove('is-open');
      if (this.opener) this.opener.setAttribute('aria-expanded', 'false');

      this.releaseSheet(returnFocus ? this.opener : null);
    }

    /* Only ever undoes a lock and a trap this element actually applied — the
       trap stack is shared with the drawer and the modal. */
    releaseSheet(returnTo) {
      if (!this.trapped) return;
      this.trapped = false;

      var helpers = utils();
      if (helpers && isFn(helpers.removeTrapFocus)) helpers.removeTrapFocus(returnTo || null);
      if (helpers && isFn(helpers.unlockScroll)) helpers.unlockScroll();
    }

    isSheet() {
      if (!this.mq) return true;
      return !!this.mq.matches;
    }
  }

  define('facet-form', FacetForm);

  /* =======================================================================
     3 — <price-range>
     Two number fields plus a dual-handle track. The fields carry the names
     and are the only part that submits; the handles write into them.
     ======================================================================= */

  class PriceRange extends FacetElement {
    connectedCallback() {
      this.minInput = qs('[data-prange-input="min"]', this);
      this.maxInput = qs('[data-prange-input="max"]', this);
      this.minHandle = qs('[data-prange-handle="min"]', this);
      this.maxHandle = qs('[data-prange-handle="max"]', this);

      if (!this.minInput || !this.maxInput) return;

      this.ceiling = toNumber(this.getAttribute('data-prange-ceiling'), 0);
      if (this.ceiling <= 0) {
        this.ceiling = Math.max(
          toNumber(this.maxInput.getAttribute('max'), 0),
          toNumber(this.maxInput.getAttribute('placeholder'), 0),
          1
        );
      }

      this.listen(this, 'input', this.onInput.bind(this));
      this.listen(this, 'change', this.onChange.bind(this));

      this.syncFromFields(false);
    }

    disconnectedCallback() {
      this.teardown();
    }

    /* ---- Reading the two sides ------------------------------------------
       An empty field means "no bound", which is not the same as zero: an
       empty minimum is 0 and an empty maximum is the ceiling, but neither is
       written back into the field, so the URL stays clean. */

    minValue() {
      var raw = String(this.minInput.value || '').trim();
      if (raw === '') return 0;
      return clamp(toNumber(raw, 0), 0, this.ceiling);
    }

    maxValue() {
      var raw = String(this.maxInput.value || '').trim();
      if (raw === '') return this.ceiling;
      return clamp(toNumber(raw, this.ceiling), 0, this.ceiling);
    }

    onInput(event) {
      var target = event.target;
      if (!target) return;

      if (target === this.minHandle || target === this.maxHandle) {
        this.fromHandles(target);
        return;
      }

      if (target === this.minInput || target === this.maxInput) {
        this.syncFromFields(false);
      }
    }

    onChange(event) {
      var target = event.target;
      if (target !== this.minInput && target !== this.maxInput) return;
      /* A typed value is only corrected once the field is left, so a person
         typing "12" on the way to "120" is not fought mid-word. */
      this.syncFromFields(true);
    }

    /** A handle moved: clamp it against its twin and write the field. */
    fromHandles(moved) {
      if (!this.minHandle || !this.maxHandle) return;

      var low = clamp(toNumber(this.minHandle.value, 0), 0, this.ceiling);
      var high = clamp(toNumber(this.maxHandle.value, this.ceiling), 0, this.ceiling);

      if (low > high) {
        if (moved === this.minHandle) low = high;
        else high = low;
        this.minHandle.value = String(low);
        this.maxHandle.value = String(high);
      }

      /* A handle parked at either end means "no bound on this side". */
      this.minInput.value = low <= 0 ? '' : String(low);
      this.maxInput.value = high >= this.ceiling ? '' : String(high);

      this.paint(low, high);

      emit(moved === this.minHandle ? this.minInput : this.maxInput, 'change');
    }

    /** A field changed: clamp both sides and move the handles to match. */
    syncFromFields(correct) {
      var low = this.minValue();
      var high = this.maxValue();

      if (low > high) {
        if (correct) {
          low = high;
          this.minInput.value = low <= 0 ? '' : String(low);
        } else {
          /* Mid-typing: paint the track with the pair the other way round
             rather than showing an inverted bar. */
          var swap = low;
          low = high;
          high = swap;
        }
      }

      if (correct) {
        if (String(this.minInput.value || '').trim() !== '') {
          this.minInput.value = String(clamp(toNumber(this.minInput.value, 0), 0, this.ceiling));
        }
        if (String(this.maxInput.value || '').trim() !== '') {
          this.maxInput.value = String(clamp(toNumber(this.maxInput.value, this.ceiling), 0, this.ceiling));
        }
      }

      if (this.minHandle) this.minHandle.value = String(low);
      if (this.maxHandle) this.maxHandle.value = String(high);

      this.paint(low, high);
    }

    /** Move the filled part of the rail under the two handles. */
    paint(low, high) {
      if (this.ceiling <= 0) return;
      var from = clamp((low / this.ceiling) * 100, 0, 100);
      var to = clamp((high / this.ceiling) * 100, 0, 100);
      this.style.setProperty('--prange-from', from + '%');
      this.style.setProperty('--prange-to', to + '%');
    }
  }

  define('price-range', PriceRange);
})();
